// src/components/layout/Banner.jsx

import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { supabase } from "../../supabaseClient";
import { getUserTotalXp } from "../../lib/learning/lessons";
import { getCurrentUserStreakSummary } from "../../lib/learning/streaks";

const LOGO_SRC =
  "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/Icon/3.png";

const LOGOUT_SRC =
  "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/MVP4/In-App/Logout.png";

const STREAK_ICON_SRC =
  "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/MVP4/In-App/Streak.png";

const XP_ICON_SRC =
  "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/MVP4/In-App/XP.png";

export default function Banner({
  homeTo = "/dashboard",
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const [totalXp, setTotalXp] =
    useState(0);

  const [streakValue, setStreakValue] =
    useState(0);

  useEffect(() => {
    let alive = true;

    async function loadBannerStats() {
      try {
        const [
          xpResult,
          streakResult,
        ] = await Promise.all([
          getUserTotalXp(supabase),
          getCurrentUserStreakSummary(),
        ]);

        if (!alive) return;

        setTotalXp(
          Number(xpResult) || 0
        );

        setStreakValue(
          Number(
            streakResult?.current_streak ??
              streakResult?.currentStreak ??
              0
          ) || 0
        );
      } catch (error) {
        console.warn(
          "Banner stats could not be loaded:",
          error
        );
      }
    }

    loadBannerStats();

    return () => {
      alive = false;
    };
  }, []);

  const showProgressStats =
    location.pathname === "/journey" ||
    location.pathname.startsWith("/journey/") ||
    location.pathname === "/resources" ||
    location.pathname.startsWith("/resources/") ||
    location.pathname === "/profile" ||
    location.pathname.startsWith("/profile/");

  async function handleLogout() {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.warn(
        "Logout failed:",
        error
      );
    } finally {
      navigate("/login", {
        replace: true,
      });
    }
  }

  return (
    <header className="relative z-40 w-full border-b border-[#EEEEEE] bg-white">
      <div
        className="mx-auto flex h-[76px] w-full max-w-[430px] items-center px-5"
        style={{
          paddingTop:
            "env(safe-area-inset-top, 0px)",
        }}
      >
        <Link
          to={homeTo}
          aria-label="Go to Home"
          className="flex shrink-0 items-center"
        >
          <img
            src={LOGO_SRC}
            alt="Gwin"
            className="h-[75px] w-[75px] object-contain"
            draggable="false"
          />
        </Link>

        <div className="ml-auto flex items-center gap-2">
          {showProgressStats && (
            <>
              <div className="flex h-[34px] min-w-[72px] items-center justify-center gap-1.5 rounded-full bg-[#FF914D] px-3 shadow-[0_1px_2px_rgba(0,0,0,0.06)]">
                <img
                  src={STREAK_ICON_SRC}
                  alt=""
                  aria-hidden="true"
                  className="h-[17px] w-[17px] object-contain"
                  draggable="false"
                />

                <span className="text-[11px] font-black text-white">
                  {streakValue}
                </span>
              </div>

              <div className="flex h-[34px] min-w-[78px] items-center justify-center gap-1.5 rounded-full bg-[#FF914D] px-3 shadow-[0_1px_2px_rgba(0,0,0,0.06)]">
                <img
                  src={XP_ICON_SRC}
                  alt=""
                  aria-hidden="true"
                  className="h-[17px] w-[17px] object-contain"
                  draggable="false"
                />

                <span className="text-[11px] font-black text-white">
                  {totalXp.toLocaleString()}
                </span>
              </div>
            </>
          )}

          <button
            type="button"
            onClick={handleLogout}
            aria-label="Log out"
            className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full transition active:scale-95"
          >
            <img
              src={LOGOUT_SRC}
              alt=""
              aria-hidden="true"
              className="h-[29px] w-[29px] object-contain"
              draggable="false"
            />
          </button>
        </div>
      </div>
    </header>
  );
}
